import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SmartImage from "@/components/SmartImage";

/**
 * Recipient-facing gift claim page. Public — the URL token is the auth.
 * If the recipient isn't signed in, we ask them to sign in / register first,
 * then redirect back here to auto-claim.
 */
export default function GiftClaim() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gift, setGift] = useState(null);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get(`/gifts/${token}`)
      .then((r) => setGift(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Gift not found"));
  }, [token]);

  const claim = async () => {
    if (!user || user === false) {
      // Ask them to sign in; return here after
      navigate("/login", { state: { from: `/gift/${token}` } });
      return;
    }
    setClaiming(true);
    try {
      const { data } = await api.post(`/gifts/${token}/claim`);
      toast.success("Gift redeemed. It's on your shelf.");
      navigate(`/read/${data.book_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim gift");
      setClaiming(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Gift className="w-12 h-12 text-muted_ink mx-auto mb-6" strokeWidth={1.25} />
        <h1 className="font-serif text-4xl">This gift isn't valid.</h1>
        <p className="text-muted_ink mt-4">{error}</p>
        <Link to="/browse" className="btn-primary mt-10 inline-flex">Browse the shelf</Link>
      </div>
    );
  }
  if (!gift) {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted_ink">Unwrapping…</div>;
  }

  const alreadyRedeemed = gift.status === "redeemed";
  const cover = gift.book?.cover_url || (gift.book?.cover_path ? `${API}/files/${gift.book.cover_path}` : null);

  return (
    <div data-testid="gift-claim-page" className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="text-center">
        <Sparkles className="w-10 h-10 text-terracotta mx-auto mb-6" strokeWidth={1.25} />
        <div className="label-eyebrow text-sage">A gift from {gift.sender_name || "a friend"}</div>
        <h1 className="font-serif text-5xl sm:text-6xl mt-3">
          Hello, <span className="text-terracotta">{gift.recipient_name || "reader"}.</span>
        </h1>
        <p className="text-muted_ink mt-4 max-w-xl mx-auto">
          {gift.sender_name || "A friend"} bought you a book. It's ready to add to your Folio shelf.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border border-edge bg-linen p-8 md:p-10">
        <div className="md:col-span-5">
          <div className="aspect-[3/4] bg-white overflow-hidden book-card-cover">
            {cover && <SmartImage src={cover} alt={gift.book?.title} className="w-full h-full object-cover" />}
          </div>
        </div>
        <div className="md:col-span-7 flex flex-col">
          <div className="label-eyebrow text-sage">{gift.book?.category}</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2" data-testid="gift-book-title">{gift.book?.title}</h2>
          <div className="italic text-muted_ink mt-1">by {gift.book?.author}</div>

          {gift.message && (
            <div className="mt-6 border-l-2 border-terracotta pl-4 py-1">
              <div className="label-eyebrow text-terracotta text-[10px]">A note</div>
              <blockquote className="font-serif italic text-lg mt-2 text-ink">"{gift.message}"</blockquote>
            </div>
          )}

          <div className="mt-auto pt-8">
            {alreadyRedeemed ? (
              <div className="space-y-4">
                <div className="text-sm text-muted_ink">This gift has already been claimed.</div>
                <Link to="/library" className="btn-primary inline-flex">Open my library</Link>
              </div>
            ) : (
              <button
                data-testid="claim-gift-btn"
                onClick={claim}
                disabled={claiming}
                className="btn-primary disabled:opacity-60"
              >
                {claiming ? "Claiming…" : (
                  <>Claim my gift <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </button>
            )}
            {!alreadyRedeemed && !user && (
              <p className="text-xs text-muted_ink mt-3">
                You'll be asked to sign in or make a free account first — takes ten seconds.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
